import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import styles from './ToolPicker.module.css';
import type { ToolOption, ToolPickerProps } from '../../types/types';
import { ExternalLink } from 'lucide-react';

function EnvBadge({ env }: { env?: string }) {
  if (!env) return null;
  const envClass: Record<string, string> = {
    prod: styles.envProd,
    test: styles.envTest,
    dev: styles.envDev,
  };
  return (
    <span className={`${styles.envBadge} ${envClass[env] ?? styles.envNone}`}>
      {env}
    </span>
  );
}

const ToolPicker: React.FC<ToolPickerProps> = ({
  options,
  selected,
  onChange,
  placeholder = 'Search tools…',
  disabled = false,
}) => {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const [listboxStyle, setListboxStyle] = useState<CSSProperties | null>(null);

  const filteredOptions = useMemo(() => {
    const q = input.toLowerCase();
    return options.filter(opt => {
      if (selected.some(s => s.id === opt.id)) return false;
      if (q) return opt.name.toLowerCase().includes(q) || (opt.description ?? '').toLowerCase().includes(q);
      return true;
    });
  }, [options, selected, input]);

  const updateListboxPosition = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const viewportPadding = 8;
    const dropdownGap = 6;
    const maxMenuHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const openUpwards = spaceBelow < 160 && spaceAbove > spaceBelow;
    const availableHeight = Math.max(120, Math.min(maxMenuHeight, (openUpwards ? spaceAbove : spaceBelow) - dropdownGap));
    setListboxStyle(
      openUpwards
        ? { position: 'fixed', left: rect.left, bottom: window.innerHeight - rect.top + dropdownGap, width: rect.width, maxHeight: availableHeight, zIndex: 1000 }
        : { position: 'fixed', top: rect.bottom + dropdownGap, left: rect.left, width: rect.width, maxHeight: availableHeight, zIndex: 1000 }
    );
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!wrapperRef.current?.contains(target) && !listboxRef.current?.contains(target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateListboxPosition();
    window.addEventListener('resize', updateListboxPosition);
    window.addEventListener('scroll', updateListboxPosition, true);
    return () => {
      window.removeEventListener('resize', updateListboxPosition);
      window.removeEventListener('scroll', updateListboxPosition, true);
    };
  }, [open, updateListboxPosition]);

  useEffect(() => {
    if (!open || filteredOptions.length === 0) { setActiveIndex(-1); return; }
    if (activeIndex >= filteredOptions.length) setActiveIndex(filteredOptions.length - 1);
  }, [activeIndex, filteredOptions.length, open]);

  const toggleItem = (item: ToolOption) => {
    const exists = selected.some(s => s.id === item.id);
    onChange(exists ? selected.filter(s => s.id !== item.id) : [...selected, item]);
    setInput('');
    setOpen(true);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && input.length === 0 && selected.length > 0) {
      event.preventDefault();
      toggleItem(selected[selected.length - 1]);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setOpen(true);
      if (filteredOptions.length > 0) setActiveIndex(i => Math.min(i + 1, filteredOptions.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (filteredOptions.length > 0) setActiveIndex(i => Math.max(i - 1, 0));
      return;
    }
    if (event.key === 'Enter' && open && activeIndex >= 0 && filteredOptions[activeIndex]) {
      event.preventDefault();
      toggleItem(filteredOptions[activeIndex]);
      return;
    }
    if (event.key === 'Escape') { setOpen(false); setActiveIndex(-1); }
  };

  const openToolInNewTab = (tool: ToolOption) => {
    const url = `/toolslist/${tool.id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div
        className={`${styles.control} ${disabled ? styles.controlDisabled : ''}`}
        onClick={() => { if (disabled) return; setOpen(true); inputRef.current?.focus(); }}
      >
        <div className={styles.valueArea}>
          {selected.map(item => (
            <span key={item.id} className={styles.selectedItem}>
              <span className={styles.selectedItemName}>{item.name}</span>
              <button
                type="button"
                className={styles.removeBtn}
                aria-label={`open tool ${item.name}`}
                onClick={e => { e.stopPropagation(); openToolInNewTab(item); }}
                disabled={disabled}
              >
              <ExternalLink size={'12px'}/>
              </button>
              <button
                type="button"
                className={styles.removeBtn}
                aria-label={`Remove ${item.name}`}
                onClick={e => { e.stopPropagation(); toggleItem(item); }}
                disabled={disabled}
              >
                &times;
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => { setInput(e.target.value); setOpen(true); setActiveIndex(-1); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={selected.length === 0 ? placeholder : ''}
            className={styles.inlineInput}
            disabled={disabled}
            aria-expanded={open}
            aria-controls={open ? listboxId : undefined}
            aria-autocomplete="list"
          />
        </div>
        {selected.length > 0 && (
          <button
            type="button"
            className={styles.clearAllBtn}
            aria-label="Clear all selected tools"
            onClick={e => { e.stopPropagation(); onChange([]); setOpen(false); setActiveIndex(-1); }}
            disabled={disabled}
          >
            Clear all
          </button>
        )}
      </div>
      {open && listboxStyle && createPortal(
        <div className={styles.suggestionsList} role="listbox" id={listboxId} style={listboxStyle} ref={listboxRef}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt, index) => (
              <button
                type="button"
                key={opt.id}
                className={`${styles.suggestionItem} ${index === activeIndex ? styles.suggestionItemActive : ''}`}
                role="option"
                aria-selected={index === activeIndex}
                onMouseDown={e => e.preventDefault()}
                onClick={() => toggleItem(opt)}
              >
                <div className={styles.optionRow}>
                  <span className={styles.optionName}>{opt.name}</span>
                  <EnvBadge env={opt.environment} />
                </div>
                {opt.description && <span className={styles.optionDesc}>{opt.description}</span>}
              </button>
            ))
          ) : (
            <div className={styles.emptySuggestions}>{input ? 'No matching tools' : 'No tools available'}</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ToolPicker;
